'use client';

import { useCallback, useEffect, useState } from 'react';
import { Input } from '@savvy-kit/components/input';

import { debounce } from '../lib/debounce';

export function DebouncedInput({
  value: initialValue,
  onChange,
  debounceMs = 500, // This is the wait time, not the function
  size = 'default',
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounceMs?: number;
  size?: 'default' | 'lg';
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'size'>) {
  const [value, setValue] = useState(initialValue);

  // Sync with initialValue when it changes
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  // Define the debounced function with useCallback
  const debouncedOnChange = useCallback(
    debounce((newValue: string | number) => {
      onChange(newValue);
    }, debounceMs), // Pass the wait time here
    [debounceMs, onChange], // Dependencies
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue); // Update local state immediately
    debouncedOnChange(newValue); // Call debounced version
  };

  return <Input {...props} value={value} onChange={handleChange} />;
}
